import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { Save, Send, Undo2, ArrowLeft, Eye, History as HistoryIcon, Link2, Sparkles } from "lucide-react";
import { FIELD_TYPES } from "./blockFields";
import { invalidateCms, substitutePricingTokens } from "@/lib/cms";

const PRICING_TOKENS = ["{one_bag_price}", "{two_bag_price}", "{extra_per_bag}", "{bin_clean_price}"];

function valueAsString(v) {
  if (typeof v === "string") return v;
  try { return JSON.stringify(v); } catch { return ""; }
}

function hasPricingToken(v) {
  const s = valueAsString(v);
  return PRICING_TOKENS.some((t) => s.includes(t));
}

export default function EditorPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [drafts, setDrafts] = useState({}); // id -> draft value (local, dirty)
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [opsSettings, setOpsSettings] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get(`/cms/pages/${slug}`);
      setPage(data.page);
      setBlocks(data.blocks);
      setDrafts({});
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to load this page";
      setLoadError(typeof msg === "string" ? msg : "Failed to load this page");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Fetch Ops pricing settings (public endpoint) so we can show the live
    // values that {extra_per_bag} / {bin_clean_price} tokens will resolve to.
    axios
      .get(`${BACKEND_URL}/api/settings`, { timeout: 8000 })
      .then((r) => setOpsSettings(r.data))
      .catch(() => setOpsSettings(null));
  }, [slug]);

  const pageHasPricingTokens = blocks.some((b) =>
    hasPricingToken(drafts[b.id] !== undefined ? drafts[b.id] : b.value_draft),
  );

  const uploadMedia = async (file, alt) => {
    const fd = new FormData();
    fd.append("file", file);
    if (alt) fd.append("alt", alt);
    const { data } = await api.post("/cms/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const onFieldChange = (blockId, newValue) => {
    setDrafts((d) => ({ ...d, [blockId]: newValue }));
  };

  const dirtyCount = Object.keys(drafts).length;

  const saveDrafts = async () => {
    if (dirtyCount === 0) {
      toast.info("Nothing to save");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        blocks: Object.entries(drafts).map(([id, value_draft]) => ({ id, value_draft })),
      };
      await api.put("/cms/blocks", payload);
      toast.success("Draft saved");
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      if (dirtyCount > 0) {
        await api.put("/cms/blocks", {
          blocks: Object.entries(drafts).map(([id, value_draft]) => ({ id, value_draft })),
        });
      }
      const { data } = await api.post(`/cms/pages/${slug}/publish`);
      toast.success(`Published ${data.published} blocks live`);
      invalidateCms();
      await load();
    } catch {
      toast.error("Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const revert = async () => {
    if (!confirm("Discard all drafts and reset to currently published values?")) return;
    try {
      await api.post(`/cms/pages/${slug}/revert`);
      toast.success("Drafts discarded");
      await load();
    } catch {
      toast.error("Revert failed");
    }
  };

  const openRevisions = async () => {
    setShowRevisions(true);
    try {
      const { data } = await api.get(`/cms/revisions/${slug}`);
      setRevisions(data);
    } catch {
      setRevisions([]);
    }
  };

  const restoreRevision = async (revId) => {
    if (!confirm("Load this snapshot into drafts? (You can still review before publishing.)")) return;
    try {
      await api.post(`/cms/revisions/${revId}/restore`);
      toast.success("Snapshot restored into drafts");
      setShowRevisions(false);
      await load();
    } catch {
      toast.error("Restore failed");
    }
  };

  if (loadError) {
    return (
      <div className="card-clay p-8 text-center" data-testid="editor-load-error">
        <div className="font-fredoka text-xl font-bold text-slate-900 mb-2">Couldn't load this page</div>
        <p className="text-sm text-slate-600 mb-4">{loadError}</p>
        <div className="flex items-center justify-center gap-2">
          <button onClick={load} className="btn-push-green px-4 py-2 text-sm" data-testid="editor-retry">Try again</button>
          <Link to="/editor" className="btn-push-outline px-4 py-2 text-sm">Back to pages</Link>
        </div>
      </div>
    );
  }
  if (loading || !page) return <div className="card-clay p-10 text-center text-slate-400" data-testid="editor-loading">Loading…</div>;

  // Group blocks by `group`
  const groups = {};
  blocks.forEach((b) => {
    const g = b.group || "General";
    if (!groups[g]) groups[g] = [];
    groups[g].push(b);
  });

  const previewHref = page.path === "/" ? "/" : page.path;

  return (
    <div data-testid={`editor-page-${slug}`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <Link to="/editor" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="editor-back-to-pages"><ArrowLeft size={12}/> All pages</Link>
          <h2 className="font-fredoka text-2xl font-bold text-slate-900 mt-1">{page.label}</h2>
          <div className="text-xs text-slate-500">
            Last published: {page.last_published_at ? new Date(page.last_published_at).toLocaleString() : "never"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={openRevisions} className="btn-push-outline px-3 py-2 text-xs inline-flex items-center gap-1" data-testid="editor-revisions-btn">
            <HistoryIcon size={14}/> Revisions
          </button>
          <a href={previewHref} target="_blank" rel="noreferrer" className="btn-push-outline px-3 py-2 text-xs inline-flex items-center gap-1" data-testid="editor-preview-btn">
            <Eye size={14}/> Preview live
          </a>
          {dirtyCount > 0 && (
            <button onClick={revert} className="btn-push-outline px-3 py-2 text-xs inline-flex items-center gap-1 text-red-700 border-red-200" data-testid="editor-revert-btn">
              <Undo2 size={14}/> Discard drafts
            </button>
          )}
          <button
            onClick={saveDrafts}
            disabled={saving || dirtyCount === 0}
            className="btn-push-outline px-3 py-2 text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="editor-save-btn"
          >
            <Save size={14}/> {saving ? "Saving…" : `Save draft${dirtyCount ? ` (${dirtyCount})` : ""}`}
          </button>
          <button
            onClick={publish}
            disabled={publishing}
            className="btn-push-green px-4 py-2 text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="editor-publish-btn"
          >
            <Send size={14}/> {publishing ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>

      {dirtyCount > 0 && (
        <div className="card-clay p-3 mb-3 bg-amber-50 border-amber-200 text-amber-900 text-sm" data-testid="editor-dirty-banner">
          You have <b>{dirtyCount}</b> unsaved change{dirtyCount === 1 ? "" : "s"}. They are local — click <b>Save draft</b> to keep them, then <b>Publish</b> to push live.
        </div>
      )}

      {pageHasPricingTokens && opsSettings && (
        <div className="card-clay p-3 mb-3 bg-blue-50 border-blue-200" data-testid="editor-sync-banner">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-xl bg-blue-500 text-white flex items-center justify-center flex-shrink-0 border-b-2 border-blue-700">
              <Link2 size={14} strokeWidth={2.5}/>
            </div>
            <div className="text-sm text-blue-900 flex-1">
              <div className="font-semibold">Synced from Operations Admin → Settings</div>
              <div className="text-xs mt-1 text-blue-800 leading-relaxed">
                This page uses live pricing tokens. The <b>numbers</b> below come from Ops Admin and stay in sync with what customers are actually charged — you can only edit the surrounding wording.
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]" data-testid="editor-sync-values">
                <TokenPill token="{one_bag_price}" value={`$${opsSettings.price_1_bag ?? 3}`} />
                <TokenPill token="{two_bag_price}" value={`$${opsSettings.price_2_bags ?? 5}`} />
                <TokenPill token="{extra_per_bag}" value={`$${opsSettings.price_extra_bag ?? 2}`} />
                <TokenPill token="{bin_clean_price}" value={`$${opsSettings.monthly_cleaning_price ?? 10}`} />
              </div>
              <Link to="/admin" className="text-[11px] font-semibold text-blue-700 hover:text-blue-900 mt-2 inline-flex items-center gap-1" data-testid="editor-open-ops">
                Open Operations Admin → Settings to change a number <Sparkles size={10}/>
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(groups).map(([group, gblocks]) => (
          <div key={group} className="card-clay p-4 sm:p-5" data-testid={`editor-group-${group.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{group}</div>
            <div className="space-y-4">
              {gblocks.map((b) => {
                const Field = FIELD_TYPES[b.type] || FIELD_TYPES.text;
                const value = drafts[b.id] !== undefined ? drafts[b.id] : b.value_draft;
                const isDirty = drafts[b.id] !== undefined;
                const isDiffFromPublished = JSON.stringify(b.value_draft) !== JSON.stringify(b.value_published);
                const tokensHere = hasPricingToken(value);
                return (
                  <div key={b.id} className={`pb-4 border-b border-slate-100 last:border-0 last:pb-0`}>
                    <div className="flex items-center justify-between mb-1.5 gap-2">
                      <label className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                        {b.label}
                        {tokensHere && (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200" title="Numbers in this field auto-sync from Ops Admin">
                            <Link2 size={9} strokeWidth={3}/> synced from ops
                          </span>
                        )}
                      </label>
                      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider">
                        {isDirty && <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">unsaved</span>}
                        {!isDirty && isDiffFromPublished && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">draft</span>}
                      </div>
                    </div>
                    <Field block={b} value={value} onChange={(v) => onFieldChange(b.id, v)} uploadFn={uploadMedia}/>
                    {tokensHere && opsSettings && typeof value === "string" && (
                      <div className="mt-1.5 text-[11px] text-slate-500" data-testid={`renders-as-${b.key}`}>
                        Renders as: <span className="text-slate-800 italic">{substitutePricingTokens(value, opsSettings)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {showRevisions && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setShowRevisions(false)} data-testid="editor-revisions-modal">
          <div className="card-clay p-5 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-fredoka text-xl font-bold">Publish history</h3>
              <button onClick={() => setShowRevisions(false)} className="p-1.5 rounded-lg hover:bg-slate-100">✕</button>
            </div>
            {revisions.length === 0 && <div className="text-sm text-slate-400">No published revisions yet.</div>}
            <ul className="space-y-2">
              {revisions.map((r) => (
                <li key={r.id} className="rounded-xl border-2 border-slate-100 p-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800">{new Date(r.created_at).toLocaleString()}</div>
                    <div className="text-xs text-slate-500">by {r.author_email || r.author_id} · {r.snapshot?.length || 0} blocks</div>
                  </div>
                  <button onClick={() => restoreRevision(r.id)} className="btn-push-outline px-3 py-1.5 text-xs" data-testid={`editor-restore-${r.id}`}>Load to drafts</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function TokenPill({ token, value }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-white border-2 border-blue-200 px-2 py-0.5">
      <code className="text-blue-700 font-mono text-[10px]">{token}</code>
      <span className="text-blue-400">→</span>
      <span className="font-bold text-blue-900">{value}</span>
    </span>
  );
}
